import React, { useState, useEffect } from 'react';
import { Menu, X, ShoppingBag, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';

export const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const { cartCount, openCart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onHide = (e: Event) => setHidden((e as CustomEvent).detail === true);
    window.addEventListener('navbar:hide', onHide as EventListener);
    return () => window.removeEventListener('navbar:hide', onHide as EventListener);
  }, []);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [menuOpen]);

  const navLinks = [
    { href: "/", label: "ראשי" },
    { href: "/about", label: "אודות" },
    { href: "/services", label: "טיפולים" },
    { href: "/shop", label: "מוצרים" },
    { href: "/personal-rx", label: "רוקחות אישית" },
    { href: "/appointment", label: "קביעת תור" },
    { href: "/contact", label: "צור קשר" },
  ];

  return (
    <>
      <nav aria-label="ניווט ראשי" className={`fixed top-0 left-0 w-full z-50 transition-all duration-700 ease-luxury ${
        hidden && !menuOpen ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
      } ${
        scrolled || menuOpen || location.pathname !== '/' ? 'bg-[#412018]/90 backdrop-blur-md py-4 shadow-lg border-b border-white/5' : 'bg-transparent py-6 md:py-8'
      }`}>
        <div className="container mx-auto px-6 flex justify-between items-center relative">
          
          {/* --- RIGHT SIDE (First in RTL): Cart & Mobile Controls --- */}
          <div className="flex items-center gap-4 md:gap-6 relative z-50">
            {/* Desktop Cart Icon */}
            <button type="button" onClick={openCart} aria-label={`פתח סל קניות${cartCount > 0 ? `, ${cartCount} פריטים` : ''}`} className="hidden xl:flex group items-center gap-2 hover:text-[#fbbf25] transition-colors relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fbbf25] focus-visible:ring-offset-2 focus-visible:ring-offset-[#412018] rounded">
              <ShoppingBag size={20} aria-hidden="true" />
              {cartCount > 0 && (
                <span aria-hidden="true" className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#fbbf25] text-[#412018] text-[10px] font-bold rounded-full transition-all duration-300">
                  {cartCount}
                </span>
              )}
            </button>

            {/* Mobile / Tablet Controls */}
            <div className="flex items-center gap-6 xl:hidden">
               {/* Mobile Cart Icon */}
               <button type="button" onClick={openCart} aria-label={`פתח סל קניות${cartCount > 0 ? `, ${cartCount} פריטים` : ''}`} className={`relative transition-colors duration-500 hover:text-[#fbbf25] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fbbf25] focus-visible:ring-offset-2 focus-visible:ring-offset-[#412018] rounded ${scrolled || menuOpen || location.pathname !== '/' ? 'text-white' : 'text-white'}`}>
                  <ShoppingBag size={22} aria-hidden="true" />
                  {cartCount > 0 && (
                    <span aria-hidden="true" className="absolute -top-2 -right-2 w-4 h-4 flex items-center justify-center bg-[#fbbf25] text-[#412018] text-[9px] font-bold rounded-full transition-all duration-300">
                      {cartCount}
                    </span>
                  )}
               </button>

               {/* Hamburger / Close Button */}
              <button 
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label={menuOpen ? 'סגור תפריט ניווט' : 'פתח תפריט ניווט'}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
                className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#fbbf25] focus-visible:ring-offset-2 focus-visible:ring-offset-[#412018] rounded transition-colors duration-500 hover:rotate-90 ${scrolled || menuOpen || location.pathname !== '/' ? 'text-[#fbbf25]' : 'text-white'}`}
              >
                {menuOpen ? <X size={28} aria-hidden="true" /> : <Menu size={28} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* --- CENTER: Desktop Navigation Links --- */}
          <div className={`hidden xl:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center gap-5 xl:gap-6 2xl:gap-8 text-[12px] 2xl:text-[13px] uppercase tracking-[0.12em] 2xl:tracking-[0.15em] font-medium whitespace-nowrap transition-colors duration-500 ${
             scrolled || location.pathname !== '/' ? 'text-white/70' : 'text-white/90 mix-blend-difference'
          }`}>
            {navLinks.map((link) => (
              <Link 
                key={link.href}
                to={link.href} 
                className="hover:text-[#fbbf25] transition-colors duration-300 relative group"
              >
                {link.label}
                <span className="absolute -bottom-2 right-0 w-0 h-[1px] bg-[#fbbf25] group-hover:w-full transition-all duration-500"></span>
              </Link>
            ))}
          </div>

          {/* --- LEFT SIDE (Last in RTL): Logo SECTION --- */}
          <div className="flex items-center gap-3 md:gap-5 relative z-50">
              {/* Logo (single image with icon + text) */}
              <Link to="/" className="flex items-center group">
                <img
                  src="https://cdn.shopify.com/s/files/1/0977/6965/2535/files/Container2_64bfd077-061d-46e2-9fff-6e0560b87ac0.png?v=1778077297"
                  alt="RIHAM קליניקה טבעית ומערבית"
                  width="245"
                  height="64"
                  className="h-10 sm:h-12 md:h-14 xl:h-16 w-auto object-contain transition-opacity duration-500 group-hover:opacity-80"
                />
              </Link>
          </div>
        </div>
      </nav>

      {/* --- MOBILE MENU OVERLAY (The "Beautiful" Navigation) --- */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label="תפריט ניווט"
        aria-hidden={!menuOpen}
        className={`fixed inset-0 bg-[#412018] z-40 flex flex-col justify-center items-center transition-all duration-1000 ease-luxury ${
          menuOpen ? 'opacity-100 visible clip-circle-full' : 'opacity-0 invisible clip-circle-zero'
        }`}
        style={{
           clipPath: menuOpen ? 'circle(150% at top right)' : 'circle(0% at top right)',
           transition: 'clip-path 1s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s ease-out'
        }}
      >
        {/* Background Texture/Gradient for Mobile Menu */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#fbbf25]/5 to-transparent pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-[#fbbf25]/10 via-transparent to-transparent pointer-events-none"></div>

        {/* Links Container */}
        <div className="flex flex-col items-center space-y-8 relative z-10 w-full px-6">
          
          {/* Explicit Close Button inside Menu (Requested) */}
          <button 
             onClick={() => setMenuOpen(false)}
             className={`absolute -top-32 right-0 flex items-center gap-2 text-[#fbbf25] text-xs uppercase tracking-widest hover:text-white transition-colors duration-300 ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}
             style={{ transitionDelay: '500ms' }}
          >
            <span>סגור</span>
            <X size={16} />
          </button>

          {navLinks.map((link, index) => (
            <Link 
              key={link.href}
              to={link.href} 
              onClick={() => setMenuOpen(false)}
              className={`text-4xl md:text-5xl font-serif text-[#EAE6D7] hover:text-[#fbbf25] hover:scale-105 transition-all duration-500 transform ${
                menuOpen ? 'translate-y-0 opacity-100 blur-0' : 'translate-y-12 opacity-0 blur-sm'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Separator */}
          <div 
            className={`w-12 h-[1px] bg-[#fbbf25]/30 my-8 transition-all duration-700 delay-500 ${
              menuOpen ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0'
            }`}
          ></div>

          {/* Extra Info */}
          <div 
            className={`flex flex-col items-center gap-6 transition-all duration-700 delay-700 ${
              menuOpen ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
             <button type="button" onClick={() => { setMenuOpen(false); openCart(); }} className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-[#fbbf25] border border-[#fbbf25]/30 px-6 py-3 hover:bg-[#fbbf25] hover:text-[#412018] transition-all duration-500">
                <ShoppingBag size={16} />
                <span>צפייה בסל הקניות</span>
             </button>
             
             <div className="text-center">
                <div className="text-[#EAE6D7]/40 text-xs tracking-widest font-sans mb-2">מושב פטיש, הנגב הצפוני</div>
                <div className="text-[#EAE6D7]/40 text-xs tracking-widest font-sans font-bold">050-1234567</div>
             </div>
          </div>
        </div>
      </div>
    </>
  );
};